import React, { useEffect, useMemo, useState } from "react";
import { useRoute, Link } from "wouter";
import { Layout } from "@/components/layout";
import { useUser } from "@clerk/react";
import { 
  useGetCourse, 
  useCreateCheckoutSession,
  useListMyEnrollments,
  getGetCourseQueryKey,
  getListMyEnrollmentsQueryKey
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Video, Users, CheckCircle2, BookOpen, FileText, Code, Calendar, Loader2 } from "lucide-react";
import { formatPriceCents, dateShortFmt, dateLongFmt } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { isRateLimitError } from "@/lib/apiErrors";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CourseExplainer } from "@/components/course-explainer/course-explainer";
import { SCRIPTS as EXPLAINER_SCRIPTS } from "@/components/course-explainer/scripts";

const enrollSchema = z.object({
  studentName: z.string().min(1, "Name is required"),
  gradeLevel: z.string().min(1, "Please select a grade level"),
  // sectionId is required for live courses; we enforce that with a runtime
  // check in onEnroll rather than a conditional schema, because the schema
  // doesn't know which course it's validating against.
  sectionId: z.string().optional(),
});

function sectionScheduleSummary(s: {
  startDate: string | null;
  endDate: string | null;
}): string {
  if (s.startDate && s.endDate) {
    return `${dateShortFmt.format(new Date(s.startDate))} → ${dateShortFmt.format(new Date(s.endDate))}`;
  }
  if (s.startDate) return `starts ${dateShortFmt.format(new Date(s.startDate))}`;
  return "Schedule TBA";
}

export default function CourseDetail() {
  const [, params] = useRoute("/courses/:slug");
  const slug = params?.slug || "";
  
  const { data: course, isLoading, isError } = useGetCourse(slug, {
    query: { enabled: !!slug, queryKey: getGetCourseQueryKey(slug) }
  });

  const [enrollOpen, setEnrollOpen] = useState(false);

  const createCheckout = useCreateCheckoutSession();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { user, isSignedIn } = useUser();

  const enrollmentsEnabled = !!isSignedIn;
  const { data: myEnrollments, isLoading: enrollmentsLoading } =
    useListMyEnrollments({
      query: {
        enabled: enrollmentsEnabled,
        queryKey: getListMyEnrollmentsQueryKey(),
      },
    });

  const isAlreadyEnrolled = !!(
    course &&
    myEnrollments?.some(
      (e) => e.course.id === course.id && e.status === "active",
    )
  );
  const isCheckingEnrollment = enrollmentsEnabled && enrollmentsLoading;

  const { control, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm({
    resolver: zodResolver(enrollSchema),
    defaultValues: { studentName: "", gradeLevel: "", sectionId: "" }
  });

  // Auto-pick a default section once the course loads so students who land on
  // a single-section live course don't have to think about it. We pick the
  // first published section that still has seats.
  const liveSections = course?.format === "live" ? course.sections ?? [] : [];
  const defaultSectionId = useMemo(() => {
    if (course?.format !== "live") return "";
    const open = (course.sections ?? []).find(
      (s) => s.status === "published" && s.seatsRemaining > 0,
    );
    return open ? String(open.id) : "";
  }, [course]);

  // Prefill enrollment form once Clerk user / course load
  useEffect(() => {
    const fullName = user
      ? [user.firstName, user.lastName].filter(Boolean).join(" ").trim()
      : "";
    reset({ studentName: fullName, gradeLevel: "", sectionId: defaultSectionId });
  }, [user, reset, defaultSectionId]);

  const onEnroll = (data: any) => {
    if (!course) return;
    if (!isSignedIn) {
      const here = window.location.pathname;
      window.location.href = `/sign-in?redirect_url=${encodeURIComponent(here)}`;
      return;
    }
    // Guard against the user blanking the section selector on a live course.
    // The server will reject it too, but failing fast here gives a nicer UX.
    if (course.format === "live" && !data.sectionId) {
      toast({
        variant: "destructive",
        title: "Pick a cohort",
        description: "Select a cohort to continue to checkout.",
      });
      return;
    }
    const sectionId =
      course.format === "live" && data.sectionId
        ? Number(data.sectionId)
        : undefined;
    const { sectionId: _drop, ...rest } = data;
    void _drop;
    createCheckout.mutate(
      { data: { courseId: course.id, ...rest, ...(sectionId ? { sectionId } : {}) } },
      {
        onSuccess: (resp) => {
          // Redirect to Stripe Checkout
          window.location.href = resp.url;
        },
        onError: (err: any) => {
          if (isRateLimitError(err)) return;
          toast({
            variant: "destructive",
            title: "Could not start checkout",
            description: err?.data?.error || "Unknown error",
          });
        },
      },
    );
  };

  if (isLoading) return <Layout><div className="p-24 text-center" role="status" aria-live="polite">Loading course&hellip;</div></Layout>;
  if (isError || !course) return (
    <Layout>
      <div className="p-24 text-center space-y-4">
        <h1 className="text-2xl font-bold">Course not found</h1>
        <p className="text-muted-foreground">The course you&rsquo;re looking for doesn&rsquo;t exist or was removed.</p>
        <Button asChild variant="outline"><Link href="/courses">Back to catalog</Link></Button>
      </div>
    </Layout>
  );

  const isLive = course.format === "live";

  return (
    <Layout>
      {/* Course Hero */}
      <div className="bg-foreground text-background py-20 md:py-28 relative overflow-hidden">
        {course.coverImageUrl && (
          <div className="absolute inset-0 opacity-15 pointer-events-none" aria-hidden="true">
            <img src={course.coverImageUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/80 to-foreground/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-grid-sm opacity-[0.06] pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-4 relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-12 items-end">
          <div className="lg:col-span-2">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-secondary mb-6 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>[ {course.topic} ]</span>
              <span className="text-background/30">/</span>
              <span>{course.level === "middle" ? "grades 6–8" : "grades 9–12"}</span>
              <span className="text-background/30">/</span>
              <span>{isLive ? "live · zoom" : "self-paced"}</span>
            </div>

            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.02] tracking-tight text-balance">
              {course.title}
            </h1>
            <p className="text-xl md:text-2xl text-background/80 mb-10 max-w-2xl font-light leading-relaxed">
              {course.tagline}
            </p>

            <div className="flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-widest text-background/80">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-secondary" aria-hidden="true" />
                <span>{course.lessonCount} lessons</span>
              </div>
              {isLive && course.durationWeeks && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-secondary" aria-hidden="true" />
                  <span>{course.durationWeeks} weeks</span>
                </div>
              )}
              {isLive && course.startDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-secondary" aria-hidden="true" />
                  <span>starts {dateShortFmt.format(new Date(course.startDate))}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Card */}
          <div className="bg-card text-card-foreground rounded-xl p-8 shadow-2xl border border-border">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">// price · usd</div>
            <div className="font-display text-5xl font-bold mb-2 tabular-nums tracking-tight">
              {formatPriceCents(course.priceCents)}
            </div>
            <p className="text-muted-foreground text-sm mb-6 border-b border-border pb-6">Full course access &amp; materials</p>
            
            {isCheckingEnrollment ? (
              <div
                className="w-full h-14 rounded-full bg-muted/60 animate-pulse motion-reduce:animate-none"
                aria-hidden="true"
              />
            ) : isAlreadyEnrolled ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                  <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
                  <span>You&rsquo;re enrolled</span>
                </div>
                <Button asChild size="lg" className="w-full h-14 text-base font-bold rounded-full">
                  <Link href={`/learn/${course.slug}`}>Open Classroom →</Link>
                </Button>
              </div>
            ) : (
            <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="w-full h-14 text-base font-bold rounded-full">
                  Enroll Now →
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] w-[calc(100vw-2rem)]">
                <DialogHeader className="min-w-0">
                  <DialogTitle className="text-2xl">Enroll in {course.title}</DialogTitle>
                  <DialogDescription>
                    {isSignedIn
                      ? "Confirm your details — we'll send you to secure checkout to finish."
                      : "Sign in first, then we'll take you to secure checkout."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit(onEnroll)} className="space-y-4 mt-4 min-w-0">
                  {isLive && liveSections.length > 0 && (
                    <div className="space-y-2 min-w-0">
                      <Label htmlFor="sectionId">Cohort</Label>
                      <Controller
                        name="sectionId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <SelectTrigger id="sectionId" className="w-full min-w-0">
                              <SelectValue placeholder="Pick a cohort" />
                            </SelectTrigger>
                            <SelectContent className="max-w-[calc(100vw-2rem)] w-[var(--radix-select-trigger-width)]">
                              {liveSections
                                .filter((s) => s.status === "published")
                                .map((s) => {
                                  const full = s.seatsRemaining <= 0;
                                  return (
                                    <SelectItem
                                      key={s.id}
                                      value={String(s.id)}
                                      disabled={full}
                                      className="whitespace-normal"
                                    >
                                      <span className="block">
                                        <span className="font-medium">{s.label}</span>
                                        <span className="block text-xs text-muted-foreground">
                                          {sectionScheduleSummary(s)} ·{" "}
                                          {full
                                            ? "full"
                                            : `${s.seatsRemaining} seat${s.seatsRemaining === 1 ? "" : "s"} left`}
                                        </span>
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {liveSections.filter((s) => s.status === "published")
                        .length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          No cohorts are open right now — check back soon.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="studentName">Student Name</Label>
                    <Controller
                      name="studentName"
                      control={control}
                      render={({ field }) => <Input {...field} id="studentName" placeholder="e.g. Jane Doe" autoComplete="name" className="w-full min-w-0" />}
                    />
                    {errors.studentName && <p className="text-sm text-destructive">{errors.studentName.message as string}</p>}
                  </div>
                  <div className="space-y-2 min-w-0">
                    <Label htmlFor="gradeLevel">Grade Level</Label>
                    <Controller
                      name="gradeLevel"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger id="gradeLevel" className="w-full min-w-0">
                            <SelectValue placeholder="Select grade" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="6th">6th Grade</SelectItem>
                            <SelectItem value="7th">7th Grade</SelectItem>
                            <SelectItem value="8th">8th Grade</SelectItem>
                            <SelectItem value="9th">9th Grade</SelectItem>
                            <SelectItem value="10th">10th Grade</SelectItem>
                            <SelectItem value="11th">11th Grade</SelectItem>
                            <SelectItem value="12th">12th Grade</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.gradeLevel && <p className="text-sm text-destructive">{errors.gradeLevel.message as string}</p>}
                  </div>
                  <Button type="submit" className="w-full mt-4" disabled={isSubmitting || createCheckout.isPending}>
                    {isSubmitting || createCheckout.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin motion-reduce:animate-none" aria-hidden="true" /> Redirecting to checkout&hellip;</>
                    ) : isSignedIn ? (
                      `Pay ${formatPriceCents(course.priceCents)} →`
                    ) : (
                      `Sign in & continue`
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    Secure checkout powered by Stripe.
                  </p>
                </form>
              </DialogContent>
            </Dialog>
            )}

            <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
              {isLive ? (
                <>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Live Zoom interactions</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Structured 8-week cohort</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Hands-on homework feedback</li>
                </>
              ) : (
                <>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Learn at your own pace</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> {course.lessonCount} high-quality video lessons</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Lifetime access to materials</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-20 grid grid-cols-1 lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-16">
          {/* About */}
          <section>
            <div className="section-tag mb-3">[ 01 ] · About this course</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-6 text-balance">What you&rsquo;ll learn.</h2>
            <div className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground leading-relaxed text-lg">
              {course.description.split('\n').filter(p => p.trim()).map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {EXPLAINER_SCRIPTS[course.slug] && (
              <div className="mt-10">
                <CourseExplainer slug={course.slug} />
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  // animated explainer · auto-plays
                </p>
              </div>
            )}
          </section>

          {/* Curriculum */}
          <section>
            <div className="section-tag mb-3">[ 02 ] · Curriculum</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8 text-balance">
              {course.lessonCount} lessons, week by <span className="font-italic-serif text-primary">week.</span>
            </h2>
            <Accordion type="single" collapsible className="w-full space-y-4">
              {course.lessons.map((lesson, idx) => (
                <AccordionItem key={lesson.id} value={`item-${lesson.id}`} className="bg-card border rounded-xl px-6 shadow-sm overflow-hidden data-[state=open]:border-primary/50 transition-colors">
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex flex-col items-start text-left gap-1">
                      <span className="text-sm font-bold text-primary uppercase tracking-wider">Lesson {lesson.orderIndex}</span>
                      <span className="text-lg font-bold">{lesson.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <p className="text-muted-foreground mb-6">{lesson.summary}</p>
                    
                    <div className="space-y-6 bg-muted/30 p-6 rounded-xl border">
                      {isLive ? (
                        <div className="flex items-center gap-3 text-sm font-medium">
                          <div className="bg-primary/10 p-2 rounded-md text-primary"><Users className="w-4 h-4" /></div>
                          {lesson.zoomScheduledAt ? (
                            <span>Live Class: {dateLongFmt.format(new Date(lesson.zoomScheduledAt))}</span>
                          ) : (
                            <span>Live class schedule TBA</span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 text-sm font-medium">
                          <div className="bg-secondary/10 p-2 rounded-md text-secondary"><Video className="w-4 h-4" /></div>
                          <span>Video Lecture ({lesson.videoDurationMinutes || '?'} min)</span>
                        </div>
                      )}

                      {lesson.readings.length > 0 && (
                        <div>
                          <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Readings</h4>
                          <ul className="space-y-2">
                            {lesson.readings.map(r => (
                              <li key={r.id} className="text-sm flex justify-between items-center bg-background border p-3 rounded-md">
                                <span>{r.title}</span>
                                <span className="text-muted-foreground text-xs">{r.estimatedMinutes} min</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {lesson.homework.length > 0 && (
                        <div>
                          <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><Code className="w-4 h-4" /> Homework</h4>
                          <ul className="space-y-2">
                            {lesson.homework.map(h => (
                              <li key={h.id} className="text-sm flex justify-between items-center bg-background border p-3 rounded-md">
                                <span>{h.title}</span>
                                <span className="text-muted-foreground text-xs">{h.estimatedMinutes} min</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        {/* Instructor */}
        <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
            <div className="section-tag mb-4">// Instructor</div>
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-dashed border-border">
              <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display text-2xl font-bold shrink-0">
                {course.instructorName.charAt(0)}
              </div>
              <div>
                <div className="font-display text-xl font-bold tracking-tight">{course.instructorName}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Lead instructor</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {course.instructorBio}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

